import { CaretLeftIcon } from "@phosphor-icons/react"
import { useHotkey } from "@tanstack/react-hotkeys"

import { IconButton } from "~/components/reusable/Button"
import { ShortcutTooltip } from "~/components/reusable/ShortcutTooltip"
import { HOTKEYS } from "~/constants/hotkeys"
import { useIsCoveredByFullPageDialog } from "~/components/reusable/dialogs/fullPageDialogLayer"
import { isEditableElement } from "~/utils/helpers"

/**
 * The drilldown stack's one way out: pops a single level, so a nested drilldown returns to its
 * parent rather than to the inspector's own content. A bare caret at the head of the inspector nav
 * row; the destination lives in the tooltip ("Back to Size split", or "Back" at the first level).
 * `Escape` pops too (`HOTKEYS.inspectorBack`), except while a text field or ag-grid cell editor
 * has focus.
 */
const InspectorBackButton = ({
  onBack,
  parentTitle,
}: {
  onBack: () => void
  /** The view being returned to, when there is one. */
  parentTitle?: string
}) => {
  const isCoveredByDialog = useIsCoveredByFullPageDialog()
  useHotkey(
    HOTKEYS.inspectorBack.hotkey,
    (event) => {
      if (isEditableElement(document.activeElement)) return
      event.preventDefault()
      onBack()
    },
    { enabled: !isCoveredByDialog },
  )

  const label = parentTitle ? `Back to ${parentTitle}` : "Back"

  return (
    <ShortcutTooltip
      label={label}
      shortcut={HOTKEYS.inspectorBack.hotkey}
      side="bottom"
      align="start"
    >
      <IconButton variant="transparent" size="xs" aria-label={label} onClick={onBack}>
        <CaretLeftIcon />
      </IconButton>
    </ShortcutTooltip>
  )
}

export default InspectorBackButton
